import React, { useState } from 'react';

const CAMPAIGNS = [
  {
    id: 1,
    name: 'April Club Activation Push',
    subject: 'Your club account is ready — post your first player request today',
    status: 'Sent',
    type: 'Promotional',
    audience: 'Clubs — In Progress',
    sent: 1240,
    opened: 682,
    clicked: 213,
    replied: 28,
    sentDate: 'Apr 1, 2026',
  },
  {
    id: 2,
    name: 'Scout Welcome Series — Week 1',
    subject: 'Welcome to TransferExpo — here\'s how to submit your first evaluation',
    status: 'Sent',
    type: 'Onboarding',
    audience: 'Scouts — New (last 7 days)',
    sent: 94,
    opened: 71,
    clicked: 48,
    replied: 12,
    sentDate: 'Mar 31, 2026',
  },
  {
    id: 3,
    name: 'Premium Upgrade Campaign',
    subject: 'Unlock Premium evaluations — clubs are waiting',
    status: 'Sent',
    type: 'Upsell',
    audience: 'Scouts — Standard Tier',
    sent: 630,
    opened: 294,
    clicked: 88,
    replied: 9,
    sentDate: 'Mar 28, 2026',
  },
  {
    id: 4,
    name: 'Re-engagement — Inactive Clubs',
    subject: 'We\'ve missed you — top players are available in your region',
    status: 'Scheduled',
    type: 'Re-engagement',
    audience: 'Clubs — Not Started (30+ days)',
    sent: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    sentDate: 'Apr 8, 2026',
  },
  {
    id: 5,
    name: 'West Africa Showcase Invite',
    subject: 'You\'re invited: West Africa Scouting Showcase — Apr 15',
    status: 'Draft',
    type: 'Event',
    audience: 'All Scouts — West Africa',
    sent: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    sentDate: '—',
  },
  {
    id: 6,
    name: 'Monthly Performance Digest — March',
    subject: 'March platform stats: 300 evals, 27 clubs activated',
    status: 'Sent',
    type: 'Newsletter',
    audience: 'All contacts',
    sent: 5159,
    opened: 2241,
    clicked: 871,
    replied: 43,
    sentDate: 'Apr 2, 2026',
  },
];

const STATUS_BADGE = {
  Sent: 'badge badge-green',
  Scheduled: 'badge badge-blue',
  Draft: 'badge badge-gray',
  Paused: 'badge badge-amber',
};

const TYPE_COLORS = {
  Promotional: '#A7F432',
  Onboarding: '#4fc3f7',
  Upsell: '#f5a623',
  'Re-engagement': '#a78bfa',
  Event: '#ff9f43',
  Newsletter: '#8a9a97',
};

const pct = (a, b) => b === 0 ? '—' : `${Math.round((a / b) * 100)}%`;

const MiniBar = ({ value, max, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ width: 60, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ width: `${max ? Math.round((value / max) * 100) : 0}%`, height: '100%', background: color, borderRadius: 2 }} />
    </div>
    <span style={{ fontSize: 12, color: 'var(--text-primary)', minWidth: 28 }}>{pct(value, max)}</span>
  </div>
);

export default function MarketingEmails({ toast }) {
  const [campaigns, setCampaigns] = useState(CAMPAIGNS);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSubject, setNewSubject] = useState('');

  const filters = ['All', 'Sent', 'Scheduled', 'Draft'];

  const filtered = campaigns.filter(c => {
    const matchFilter = filter === 'All' || c.status === filter;
    const matchSearch = search === '' || c.name.toLowerCase().includes(search.toLowerCase()) || c.subject.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const totalSent    = campaigns.filter(c => c.status === 'Sent').reduce((s, c) => s + c.sent, 0);
  const totalOpened  = campaigns.filter(c => c.status === 'Sent').reduce((s, c) => s + c.opened, 0);
  const totalClicked = campaigns.filter(c => c.status === 'Sent').reduce((s, c) => s + c.clicked, 0);
  const scheduled    = campaigns.filter(c => c.status === 'Scheduled').length;

  const createCampaign = () => {
    if (!newName.trim()) return;
    const c = {
      id: Date.now(), name: newName, subject: newSubject || '(No subject)',
      status: 'Draft', type: 'Promotional', audience: 'All contacts',
      sent: 0, opened: 0, clicked: 0, replied: 0, sentDate: '—',
    };
    setCampaigns(prev => [c, ...prev]);
    setCreating(false); setNewName(''); setNewSubject('');
    toast?.('Campaign created as Draft');
  };

  const duplicate = (id) => {
    const c = campaigns.find(c => c.id === id);
    if (!c) return;
    const copy = { ...c, id: Date.now(), name: `${c.name} (Copy)`, status: 'Draft', sent: 0, opened: 0, clicked: 0, replied: 0, sentDate: '—' };
    setCampaigns(prev => [copy, ...prev]);
    toast?.('Campaign duplicated');
  };

  const deleteCampaign = (id) => {
    setCampaigns(prev => prev.filter(c => c.id !== id));
    if (selected === id) setSelected(null);
    toast?.('Campaign deleted');
  };

  const sel = campaigns.find(c => c.id === selected);

  return (
    <div>
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Emails sent</div>
          <div className="stat-value">{totalSent.toLocaleString()}</div>
          <div className="stat-sub">All campaigns</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open rate</div>
          <div className="stat-value">{pct(totalOpened, totalSent)}</div>
          <div className="stat-sub stat-up">{totalOpened.toLocaleString()} opened</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Click rate</div>
          <div className="stat-value">{pct(totalClicked, totalSent)}</div>
          <div className="stat-sub">{totalClicked.toLocaleString()} clicks</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Scheduled</div>
          <div className="stat-value">{scheduled}</div>
          <div className="stat-sub stat-up">Queued to send</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
          Email campaigns
        </h3>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Create campaign</button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10, fontFamily: 'var(--font-ui)' }}>New campaign</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
            <input
              className="search-input"
              placeholder="Campaign name (e.g. May Club Push)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
            <input
              className="search-input"
              placeholder="Subject line (optional)"
              value={newSubject}
              onChange={e => setNewSubject(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={createCampaign}>Create draft</button>
            <button className="btn btn-ghost" onClick={() => { setCreating(false); setNewName(''); setNewSubject(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filter + Search */}
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <input
          className="search-input"
          placeholder="Search campaigns..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="filter-pills" style={{ marginBottom: 16 }}>
        {filters.map(f => (
          <button key={f} className={`pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {/* Campaign list + detail */}
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '420px 1fr' : '1fr', gap: 16, alignItems: 'start' }}>
        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-dim)' }}>
              No campaigns match your filters
            </div>
          )}
          {filtered.map(c => (
            <div
              key={c.id}
              className="card"
              style={{ cursor: 'pointer', borderColor: selected === c.id ? 'var(--accent)' : undefined, transition: 'border-color 0.15s' }}
              onClick={() => setSelected(selected === c.id ? null : c.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLORS[c.type] || '#8a9a97', flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', paddingLeft: 16 }}>{c.subject}</div>
                </div>
                <span className={STATUS_BADGE[c.status] || 'badge badge-gray'}>{c.status}</span>
              </div>

              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-muted)', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-dim)' }}>{c.type}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.audience}</span>
                <span style={{ color: 'var(--text-dim)', flexShrink: 0 }}>{c.sentDate}</span>
              </div>

              {c.status === 'Sent' && (
                <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 12 }}>
                  <span><strong style={{ color: 'var(--text-primary)' }}>{c.sent.toLocaleString()}</strong> <span style={{ color: 'var(--text-dim)' }}>sent</span></span>
                  <span><strong style={{ color: '#A7F432' }}>{pct(c.opened, c.sent)}</strong> <span style={{ color: 'var(--text-dim)' }}>open</span></span>
                  <span><strong style={{ color: '#4fc3f7' }}>{pct(c.clicked, c.sent)}</strong> <span style={{ color: 'var(--text-dim)' }}>click</span></span>
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 12 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => toast?.('Email editor coming soon')}>
                  {c.status === 'Draft' ? 'Edit' : 'View'}
                </button>
                <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => duplicate(c.id)}>
                  Duplicate
                </button>
                {c.status === 'Draft' && (
                  <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px', color: 'var(--accent)', borderColor: 'rgba(167,244,50,0.3)' }} onClick={() => toast?.('Campaign scheduled')}>
                    Schedule
                  </button>
                )}
                <button className="btn btn-danger" style={{ fontSize: 11, padding: '4px 10px', marginLeft: 'auto' }} onClick={() => deleteCampaign(c.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {sel && (
          <div className="card" style={{ position: 'sticky', top: 0 }}>
            <div className="card-header" style={{ marginBottom: 20 }}>
              <span className="card-title">Campaign details</span>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setSelected(null)}>✕ Close</button>
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {[
                ['Name', sel.name],
                ['Subject', sel.subject],
                ['Type', sel.type],
                ['Audience', sel.audience],
                ['Status', null],
                ['Date sent', sel.sentDate],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', minWidth: 70, paddingTop: 2 }}>{label}</span>
                  {label === 'Status'
                    ? <span className={STATUS_BADGE[sel.status] || 'badge badge-gray'}>{sel.status}</span>
                    : <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{val}</span>
                  }
                </div>
              ))}
            </div>

            {/* Metrics */}
            {sel.status === 'Sent' && (
              <>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginBottom: 16 }}>
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>Performance</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {[
                      { label: 'Open rate', value: sel.opened, color: '#A7F432' },
                      { label: 'Click rate', value: sel.clicked, color: '#4fc3f7' },
                      { label: 'Reply rate', value: sel.replied, color: '#f5a623' },
                    ].map(({ label, value, color }) => (
                      <div key={label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                          <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value.toLocaleString()} ({pct(value, sel.sent)})</span>
                        </div>
                        <MiniBar value={value} max={sel.sent} color={color} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary numbers */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'Delivered', val: sel.sent.toLocaleString() },
                    { label: 'Opened', val: sel.opened.toLocaleString() },
                    { label: 'Clicked', val: sel.clicked.toLocaleString() },
                    { label: 'Replied', val: sel.replied.toLocaleString() },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 20, fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--text-primary)' }}>{val}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {sel.status === 'Draft' && (
              <div style={{ background: 'rgba(167,244,50,0.06)', border: '1px solid rgba(167,244,50,0.15)', borderRadius: 10, padding: '16px', marginTop: 4 }}>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>This campaign is a draft and hasn't been sent yet.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => toast?.('Email editor coming soon')}>Edit content</button>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => toast?.('Campaign scheduled')}>Schedule send</button>
                </div>
              </div>
            )}

            {sel.status === 'Scheduled' && (
              <div style={{ background: 'rgba(79,195,247,0.06)', border: '1px solid rgba(79,195,247,0.2)', borderRadius: 10, padding: '16px', marginTop: 4 }}>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Scheduled to send on <strong style={{ color: 'var(--text-primary)' }}>{sel.sentDate}</strong></p>
                <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>To: {sel.audience}</p>
                <button className="btn btn-ghost" style={{ fontSize: 12, marginTop: 12 }} onClick={() => toast?.('Campaign unscheduled and moved to draft')}>Unschedule</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
